import { createStore } from 'vuex';
import apiService from '@/services/api.service';
import { setItem, getItem, removeItem } from '@/services/storage.service';

export default createStore({
  state: {
    // Auth related state
    token: '',  // JWT is in HttpOnly cookie, not accessible here
    user: getItem('user_data', true) || {},
    
    // UI state
    loading: false,
    error: null
  },
  getters: {
    isAuthenticated: state => !!state.user.email,
    currentUser: state => state.user,
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    getError: state => state.error
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_AUTH(state) {
      state.token = '';
      state.user = {};
    }
  },
  actions: {
    // Auth actions
    async login({ commit }, credentials) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await apiService.login(credentials);
        console.log('Login response:', response.data);
        
        // The API now sets the JWT in an HttpOnly cookie
        // We just need to store the user info we get back
        const userData = response.data;

        // Create user object with normalized properties
        const user = {
          id: userData.Id || userData.id,
          email: userData.Email || userData.email || credentials.Email
        };
        
        // Store user data in localStorage
        setItem('user_data', user);
        
        // Update state - this is critical for reactive UI updates
        //commit('SET_TOKEN', 'http-only'); // Just a placeholder since we can't access the actual token
        commit('SET_USER', user);
        return user;
      } catch (error) {
        console.error('Login error:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Authentication failed');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async logout({ commit, state }) {
      try {

        // Get the userId from state
        const userId = state.user?.id;

        // Call logout endpoint to clear the HttpOnly cookie on the server
        if (userId) {
          await apiService.logout({ userId });
        } else {
          await apiService.logout({}); // Fallback if no userId
        }
      } catch (error) {
        console.error('Error during logout:', error);
      }
      
      // Clear our user data from localStorage
      removeItem('user_data');
      commit('CLEAR_AUTH');
    },
    
    async register({ commit }, userData) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await apiService.register(userData);
        console.log('Registration response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Registration error:', error);
        commit('SET_ERROR', error.response?.data?.message || 'Registration failed');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    // Search functionality
    async search({ commit }, searchParams) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await apiService.search(searchParams);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || 'Search failed');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
  }
});
